// src/components/UserProfile.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { auth, db, storage } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { SIGN_IN_PROVIDER_MAP } from '../constants';
import { 
  userService, 
  SUPPORTED_LOCALES, 
  SUMMARY_TYPES, 
  EDITION_CONFIG 
} from '../services/UserService';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import InlineEdit from './common/InlineEdit';
import {
  UserCircleIcon,
  EnvelopeIcon,
  LanguageIcon,
  DocumentTextIcon,
  BellIcon,
  LinkIcon,
  CreditCardIcon,
  PlusIcon,
  XMarkIcon,
  TrashIcon,
  PencilIcon,
  CameraIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowPathIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  ArrowTopRightOnSquareIcon,
  UserMinusIcon,
  ClipboardDocumentIcon,
  ComputerDesktopIcon
} from '@heroicons/react/24/outline';

// ======= Constants =======
const PRIVACY_POLICY_URL = 'https://app.termly.io/policy-viewer/policy.html?policyUUID=349e246d-abec-4caf-a8fb-603b1cf0b205';
const TERMS_OF_SERVICE_URL = 'https://app.termly.io/policy-viewer/policy.html?policyUUID=f19e2838-e2bb-487b-8a95-e6d8269026cc';
const SUPPORT_EMAIL = 'support@notemail.ai';
const DELETE_ACCOUNT_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSftb9AGCZMJto7URQSEduSTUSUlb02JVzoofBEHsJHa3EK47w/viewform';

// ======= Sub-Components =======

/**
 * Info Icon component - shows a question mark in a circle
 */
function InfoIcon({ onClick }) {
  const { t } = useTranslation();
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="w-5 h-5 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
      title={t('common.moreInformation')}
    >
      <span className="text-xs font-semibold text-gray-600">?</span>
    </button>
  );
}

InfoIcon.propTypes = {
  onClick: PropTypes.func.isRequired
};

/**
 * Info Modal component
 */
function InfoModal({ isOpen, onClose, title, content }) {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="text-sm text-gray-700 whitespace-pre-wrap">
          {content}
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-white bg-[#F07807] rounded-lg hover:bg-[#d66a05] transition-colors"
          >
            {t('common.gotIt')}
          </button>
        </div>
      </div>
    </div>
  );
}

InfoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired
};

/**
 * Section wrapper component for consistent styling
 */
function SettingsSection({ title, icon: Icon, children, defaultOpen = true, infoContent, onInfoClick }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-5 w-5 text-orange-500" />}
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {infoContent && (
            <InfoIcon onClick={onInfoClick} />
          )}
        </div>
        {isOpen ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-3 pb-3 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
}

SettingsSection.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  children: PropTypes.node,
  defaultOpen: PropTypes.bool,
  infoContent: PropTypes.string,
  onInfoClick: PropTypes.func
};

/**
 * Toggle switch component
 */
function Toggle({ enabled, onChange, label, description }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!enabled)}
        className={`${
          enabled ? 'bg-[#F07807]' : 'bg-gray-200'
        } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2`}
      >
        <span
          className={`${
            enabled ? 'translate-x-5' : 'translate-x-0'
          } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
        />
      </button>
    </div>
  );
}

Toggle.propTypes = {
  enabled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string
};

/**
 * Clickable row for links
 */
function LinkRow({ icon: Icon, label, href, danger = false }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center justify-between py-2.5 px-1 hover:bg-gray-50 rounded-lg transition-colors ${danger ? 'text-red-600' : 'text-gray-700'}`}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon className={`h-4 w-4 ${danger ? 'text-red-500' : 'text-gray-400'}`} />}
        <span className="text-sm">{label}</span>
      </div>
      <ArrowTopRightOnSquareIcon className={`h-4 w-4 ${danger ? 'text-red-400' : 'text-gray-400'}`} />
    </a>
  );
}

LinkRow.propTypes = {
  icon: PropTypes.elementType,
  label: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  danger: PropTypes.bool
};

/**
 * Keyword tag component
 */
function KeywordTag({ keyword, onRemove }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 text-sm font-medium">
      {keyword}
      <button
        onClick={onRemove}
        className="ml-0.5 hover:text-orange-900 transition-colors"
        title={t('userProfile.removeKeyword')}
      >
        <XMarkIcon className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

KeywordTag.propTypes = {
  keyword: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired
};

/**
 * Secondary email row component
 */
function SecondaryEmailRow({ email, onRemove, isValid }) {
  const { t } = useTranslation();
  return (
    <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${isValid ? 'bg-gray-50' : 'bg-red-50 border border-red-200'}`}>
      <EnvelopeIcon className={`h-4 w-4 flex-shrink-0 ${isValid ? 'text-gray-400' : 'text-red-400'}`} />
      <span className={`flex-1 text-sm ${isValid ? 'text-gray-700' : 'text-red-700'}`}>{email}</span>
      <button
        onClick={onRemove}
        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
        title={t('userProfile.removeEmail')}
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

SecondaryEmailRow.propTypes = {
  email: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired,
  isValid: PropTypes.bool
};

/**
 * Webhook log entry component
 */
function WebhookLogEntry({ log }) {
  const { t } = useTranslation();
  const timestamp = log.timestamp?.toDate?.() || new Date(log.timestamp);
  const isSuccess = log.status === 'success';
  
  return (
    <div className={`p-2.5 rounded-lg border ${isSuccess ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          {isSuccess ? (
            <CheckIcon className="h-4 w-4 text-green-600" />
          ) : (
            <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
          )}
          <span className={`text-sm font-medium ${isSuccess ? 'text-green-700' : 'text-red-700'}`}>
            {isSuccess ? t('userProfile.success') : t('userProfile.error')}
          </span>
        </div>
        <span className="text-xs text-gray-500">
          {timestamp.toLocaleString()}
        </span>
      </div>
      {log.responseCode && (
        <p className="text-xs text-gray-600">{t('userProfile.statusCode')} {log.responseCode}</p>
      )}
      {log.responseTimeMs && (
        <p className="text-xs text-gray-600">{t('userProfile.responseTime')} {log.responseTimeMs}ms</p>
      )}
      {log.errorMessage && (
        <p className="text-xs text-red-600 mt-1">{log.errorMessage}</p>
      )}
    </div>
  );
}

WebhookLogEntry.propTypes = {
  log: PropTypes.object.isRequired
};

// ======= Main Component =======

function UserProfile() {
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [profilePicUrl, setProfilePicUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState({});
  
  // Editable fields state
  const [displayName, setDisplayName] = useState('');
  const [sendEmailAddress, setSendEmailAddress] = useState('');
  const [secondaryEmails, setSecondaryEmails] = useState([]);
  const [newSecondaryEmail, setNewSecondaryEmail] = useState('');
  const [aboutMe, setAboutMe] = useState('');
  const [hintKeywords, setHintKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [userLocale, setUserLocale] = useState('en');
  const [appInterfaceLanguageLocale, setAppInterfaceLanguageLocale] = useState('en');
  const [defaultSummaryType, setDefaultSummaryType] = useState('standard');
  const [defaultSendEmail, setDefaultSendEmail] = useState(true);
  const [addTranscriptInEmail, setAddTranscriptInEmail] = useState(false);
  
  // Webhook state
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookURL, setWebhookURL] = useState('');
  const [webhookStats, setWebhookStats] = useState({});
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [showWebhookLogs, setShowWebhookLogs] = useState(false);
  
  // Modal states
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [showAboutMeModal, setShowAboutMeModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState({ title: '', content: '' });
  
  // File input ref
  const fileInputRef = useRef(null);

  const handleInfoClick = (section) => {
    const infoMap = {
      emailConfig: { 
        title: t('userProfileInfo.emailConfiguration'), 
        content: t('userProfileInfo.emailConfigDesc') 
      },
      application: { 
        title: t('userProfileInfo.applicationSettings'), 
        content: t('userProfileInfo.applicationSettingsDesc') 
      },
      aboutMe: { 
        title: t('userProfileInfo.aboutMeKeywords'), 
        content: t('userProfileInfo.aboutMeKeywordsDesc') 
      },
      noteProcessing: { 
        title: t('userProfileInfo.noteProcessingInfo'), 
        content: t('userProfileInfo.noteProcessingDesc') 
      },
      emailPreferences: { 
        title: t('userProfileInfo.emailPreferencesInfo'), 
        content: t('userProfileInfo.emailPreferencesDesc') 
      },
      webhooks: { 
        title: t('userProfileInfo.webhooksInfo'), 
        content: t('userProfileInfo.webhooksDesc') 
      }
    };
    const content = infoMap[section];
    if (content) {
      setInfoModalContent(content);
      setShowInfoModal(true);
    }
  };

  // Validation helper
  const isValidEmail = useCallback((email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email.trim());
  }, []);

  // Load user data
  useEffect(() => {
    if (!auth.currentUser) return;

    const userDocRef = doc(db, 'users', auth.currentUser.uid);
    
    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(userDocRef, async (userDoc) => {
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
        
        // Update local state
        setDisplayName(data.displayName || '');
        setSendEmailAddress(data.sendEmailAddress || auth.currentUser.email || '');
        setSecondaryEmails(data.secondarySendEmails || []);
        setAboutMe(data.aboutMe || '');
        setHintKeywords(data.hintKeywords || []);
        setUserLocale(data.userLocale || 'en');
        setAppInterfaceLanguageLocale(data.appInterfaceLanguageLocale || 'en');
        setDefaultSummaryType(data.defaultSummaryType || 'standard');
        setDefaultSendEmail(data.defaultSendEmail ?? true);
        setAddTranscriptInEmail(data.addTranscriptInEmail ?? false);
        
        // Webhook data
        const webhook = data.webhook || {};
        setWebhookEnabled(webhook.enabled || false);
        setWebhookURL(webhook.principalWebhook || '');
        setWebhookStats({
          executions: webhook.executions || 0,
          errors: webhook.errors || 0,
          lastError: webhook.lastError,
          lastExecutionDate: webhook.lastExecutionDate,
          lastExecutionResult: webhook.lastExecutionResult
        });
      }
      setIsLoading(false);
    });

    // Fetch profile picture
    getDownloadURL(ref(storage, `userPictures/${auth.currentUser.uid}.jpg`))
      .then(setProfilePicUrl)
      .catch(() => setProfilePicUrl(null));

    return () => unsubscribe();
  }, []);

  // ======= Save Handlers =======

  const handleSave = async (field, saveFn, showToast = true) => {
    setIsSaving(prev => ({ ...prev, [field]: true }));
    try {
      await saveFn();
      if (showToast) toast.success(t('common.saved'));
    } catch (error) {
      console.error(`Error saving ${field}:`, error);
      toast.error(error.message || t('common.failedToSave'));
      throw error;
    } finally {
      setIsSaving(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleDisplayNameSave = async (newValue) => {
    setDisplayName(newValue);
    await handleSave('displayName', () => userService.updateDisplayName(newValue), false);
  };

  const handleSendEmailSave = async (newValue) => {
    if (newValue && !isValidEmail(newValue)) {
      toast.error(t('common.pleaseEnterValidEmail'));
      throw new Error('Invalid email');
    }
    setSendEmailAddress(newValue);
    await handleSave('sendEmailAddress', () => userService.updateSendEmailAddress(newValue), false);
  };

  const handleAddSecondaryEmail = () => {
    const email = newSecondaryEmail.trim();
    if (!email) return;
    
    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (secondaryEmails.includes(email)) {
      toast.error(t('userProfile.emailAlreadyAdded'));
      return;
    }
    
    const updated = [...secondaryEmails, email];
    setSecondaryEmails(updated);
    setNewSecondaryEmail('');
    handleSave('secondaryEmails', () => userService.updateSecondaryEmails(updated), false);
  };

  const handleRemoveSecondaryEmail = (emailToRemove) => {
    const updated = secondaryEmails.filter(e => e !== emailToRemove);
    setSecondaryEmails(updated);
    handleSave('secondaryEmails', () => userService.updateSecondaryEmails(updated), false);
  };

  const handleAboutMeSave = async (newValue) => {
    setAboutMe(newValue);
    await handleSave('aboutMe', () => userService.updateAboutMe(newValue), false);
    setShowAboutMeModal(false);
  };

  const handleAddKeyword = () => {
    const keyword = newKeyword.trim();
    if (!keyword) return;
    
    if (hintKeywords.includes(keyword)) {
      toast.error(t('userProfile.keywordAlreadyExists'));
      return;
    }
    
    const updated = [...hintKeywords, keyword];
    setHintKeywords(updated);
    setNewKeyword('');
    setShowKeywordModal(false);
    handleSave('hintKeywords', () => userService.updateHintKeywords(updated), false);
  };

  const handleRemoveKeyword = (keywordToRemove) => {
    const updated = hintKeywords.filter(k => k !== keywordToRemove);
    setHintKeywords(updated);
    handleSave('hintKeywords', () => userService.updateHintKeywords(updated), false);
  };

  const handleLocaleSave = (newLocale) => {
    setUserLocale(newLocale);
    handleSave('userLocale', () => userService.updateUserLocale(newLocale), false);
  };

  const handleAppInterfaceLanguageSave = (newLocale) => {
    setAppInterfaceLanguageLocale(newLocale);
    handleSave('appInterfaceLanguageLocale', () => userService.updateField('appInterfaceLanguageLocale', newLocale), false);
  };

  const handleSummaryTypeSave = (newType) => {
    setDefaultSummaryType(newType);
    handleSave('defaultSummaryType', () => userService.updateDefaultSummaryType(newType), false);
  };

  const handleDefaultSendEmailToggle = (enabled) => {
    setDefaultSendEmail(enabled);
    handleSave('defaultSendEmail', () => userService.updateDefaultSendEmail(enabled), false);
  };

  const handleTranscriptToggle = (enabled) => {
    setAddTranscriptInEmail(enabled);
    handleSave('addTranscriptInEmail', () => userService.updateAddTranscriptInEmail(enabled), false);
  };

  const handleWebhookEnabledToggle = (enabled) => {
    setWebhookEnabled(enabled);
    handleSave('webhookEnabled', () => userService.updateWebhookEnabled(enabled), false);
  };

  const handleWebhookURLSave = async (newValue) => {
    setWebhookURL(newValue);
    await handleSave('webhookURL', () => userService.updateWebhookURL(newValue), false);
  };

  // Profile picture upload
  const handleProfilePictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('userProfile.pleaseSelectImageFile'));
      return;
    }

    // Validate file size (1MB max)
    if (file.size > 1 * 1024 * 1024) {
      toast.error(t('userProfile.imageMustBeLessThan1MB'));
      return;
    }

    setIsSaving(prev => ({ ...prev, profilePicture: true }));
    try {
      const url = await userService.uploadProfilePicture(file);
      setProfilePicUrl(url);
      toast.success(t('userProfile.profilePictureUpdated'));
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast.error(error.message || t('userProfile.failedToUploadProfilePicture'));
    } finally {
      setIsSaving(prev => ({ ...prev, profilePicture: false }));
    }
  };

  // Fetch webhook logs
  const handleFetchWebhookLogs = async () => {
    setIsLoadingLogs(true);
    try {
      const logs = await userService.fetchWebhookLogs(20);
      setWebhookLogs(logs);
      setShowWebhookLogs(true);
    } catch (error) {
      console.error('Error fetching webhook logs:', error);
      toast.error(t('userProfile.failedToLoadWebhookLogs'));
    } finally {
      setIsLoadingLogs(false);
    }
  };

  // Copy user ID to clipboard
  const handleCopyUserId = async () => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    
    try {
      await navigator.clipboard.writeText(userId);
      toast.success(t('userProfile.userIdCopiedToClipboard'));
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error(t('userProfile.failedToCopyUserId'));
    }
  };

  // ======= Render Helpers =======

  const UserAvatar = () => {
    const initials = displayName ? displayName.charAt(0).toUpperCase() : '?';
    
    return (
      <div className="relative">
        <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
          {profilePicUrl ? (
            <img src={profilePicUrl} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <span className="text-xl font-bold text-gray-500">{initials}</span>
          )}
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isSaving.profilePicture}
          className="absolute bottom-0 right-0 p-1 bg-[#F07807] rounded-full text-white hover:bg-[#d66a05] transition-colors shadow-lg disabled:opacity-50"
          title={t('common.changeProfilePicture')}
        >
          {isSaving.profilePicture ? (
            <ArrowPathIcon className="h-3 w-3 animate-spin" />
          ) : (
            <CameraIcon className="h-3 w-3" />
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleProfilePictureChange}
          className="hidden"
        />
      </div>
    );
  };

  const EditionBadge = () => {
    const edition = userData?.edition || 'free';
    const config = EDITION_CONFIG[edition] || EDITION_CONFIG.free;
    
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold text-white ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // ======= Loading State =======

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  // ======= Main Render =======

  return (
    <div className="max-w-2xl mx-auto py-3 px-1 space-y-3">
      {/* Header */}
      <div className="hidden md:block mb-3 px-2">
        <h1 className="text-2xl font-bold text-gray-900">{t('userProfile.settings')}</h1>
      </div>

      {/* Profile Section */}
      <SettingsSection title={t('userProfile.profile')} icon={UserCircleIcon}>
        <div className="pt-2.5 space-y-3">
          {/* Avatar and Basic Info */}
          <div className="flex flex-col sm:flex-row items-start gap-3">
            <UserAvatar />
            <div className="flex-1 w-full">
              {/* Name and Sign in provider */}
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <InlineEdit
                  value={displayName}
                  onSave={handleDisplayNameSave}
                  placeholder={t('userProfile.yourName')}
                  className="text-xl font-bold text-gray-900"
                  textAlign="left"
                  multiline={false}
                  noPadding
                />
                <span className="text-xs text-gray-400">
                  {t('userProfile.signIn')} ({SIGN_IN_PROVIDER_MAP[userData?.signInProvider] || t('common.unknown')})
                </span>
              </div>

              {/* Edition Badge */}
              <div className="flex items-center gap-2 mb-2">
                <EditionBadge />
              </div>

              {/* Email below Founder badge */}
              <div className="mb-2">
                <span className="text-sm text-gray-500">
                  {userData?.email || auth.currentUser?.email}
                </span>
              </div>

              {/* User ID - Right aligned with copy icon */}
              <div className="flex items-center justify-end gap-2 mt-2">
                <span className="text-xs text-gray-400 font-mono">
                  {auth.currentUser?.uid || 'N/A'}
                </span>
                <button
                  onClick={handleCopyUserId}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  title={t('common.copyUserId')}
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </SettingsSection>

      {/* Email Configuration */}
      <SettingsSection 
        title={t('userProfileInfo.emailConfiguration')} 
        icon={EnvelopeIcon}
        infoContent={t('userProfileInfo.emailConfigDesc')}
        onInfoClick={() => handleInfoClick('emailConfig')}
      >
        <div className="pt-2.5 space-y-3">
          {/* Primary Email - Editable Field */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              {t('userProfile.primaryEmailLabel')}
            </label>
            <div className="relative flex items-center gap-2">
              <input
                type="email"
                value={sendEmailAddress}
                onChange={(e) => setSendEmailAddress(e.target.value)}
                onBlur={() => {
                  if (sendEmailAddress && sendEmailAddress !== (userData?.sendEmailAddress || auth.currentUser?.email || '')) {
                    handleSendEmailSave(sendEmailAddress);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                  }
                  if (e.key === 'Escape') {
                    setSendEmailAddress(userData?.sendEmailAddress || auth.currentUser?.email || '');
                    e.target.blur();
                  }
                }}
                className="flex-1 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white hover:border-gray-400 transition-colors"
                placeholder="your@email.com"
              />
              <PencilIcon className="h-4 w-4 text-gray-400 flex-shrink-0 pointer-events-none" />
            </div>
            <p className="text-xs text-gray-400 mt-1">{t('userProfile.pressEnterToSave')}</p>
          </div>

          {/* Secondary Emails */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              {t('userProfile.additionalEmailAddresses')}
            </label>
            
            {/* List of secondary emails */}
            <div className="space-y-1.5 mb-2">
              {secondaryEmails.map((email, index) => (
                <SecondaryEmailRow
                  key={index}
                  email={email}
                  onRemove={() => handleRemoveSecondaryEmail(email)}
                  isValid={isValidEmail(email)}
                />
              ))}
            </div>
            
            {/* Add new secondary email */}
            <div className="flex gap-2">
              <input
                type="email"
                value={newSecondaryEmail}
                onChange={(e) => setNewSecondaryEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddSecondaryEmail()}
                className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder={t('userProfile.addAnotherEmail')}
              />
              <button
                onClick={handleAddSecondaryEmail}
                disabled={!newSecondaryEmail.trim()}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors flex items-center gap-1"
              >
                <PlusIcon className="h-4 w-4" />
                {t('common.add')}
              </button>
            </div>
          </div>
        </div>
      </SettingsSection>

      {/* Application Settings */}
      <SettingsSection
        title={t('userProfile.application')}
        icon={ComputerDesktopIcon}
        infoContent={t('userProfileInfo.applicationSettingsDesc')}
        onInfoClick={() => handleInfoClick('application')}
      >
        <div className="pt-2.5 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('userProfile.appLanguage')}</label>
            <select
              value={appInterfaceLanguageLocale}
              onChange={(e) => handleAppInterfaceLanguageSave(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
            >
              <option value="en">{t('userProfile.english')}</option>
              <option value="es">{t('userProfile.spanish')}</option>
            </select>
          </div>
        </div>
      </SettingsSection>

      {/* About Me & Keywords */}
      <SettingsSection 
        title={t('userProfile.aboutMeKeywords')} 
        icon={DocumentTextIcon}
        infoContent={t('userProfileInfo.aboutMeKeywordsDesc')}
        onInfoClick={() => handleInfoClick('aboutMe')}
      >
        <div className="pt-2.5 space-y-3">
          {/* About Me */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-gray-500">{t('userProfile.aboutMe')}</label>
              <button
                onClick={() => setShowAboutMeModal(true)}
                className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-0.5"
              >
                <PencilIcon className="h-3.5 w-3.5" />
                {t('common.edit')}
              </button>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg min-h-[50px]">
              {aboutMe ? (
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{aboutMe}</p>
              ) : (
                <p className="text-sm text-gray-400 italic">{t('userProfile.noDescriptionAdded')}</p>
              )}
            </div>
          </div>

          {/* Keywords */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-gray-500">{t('userProfile.hintKeywords')}</label>
              <button
                onClick={() => setShowKeywordModal(true)}
                className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-0.5"
              >
                <PlusIcon className="h-3.5 w-3.5" />
                {t('common.add')}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {hintKeywords.length > 0 ? (
                hintKeywords.map((keyword, index) => (
                  <KeywordTag
                    key={index}
                    keyword={keyword}
                    onRemove={() => handleRemoveKeyword(keyword)}
                  />
                ))
              ) : (
                <p className="text-sm text-gray-400 italic">{t('userProfile.noKeywordsAdded')}</p>
              )}
            </div>
          </div>
        </div>
      </SettingsSection>

      {/* Note Processing Options */}
      <SettingsSection 
        title={t('userProfile.noteProcessing')} 
        icon={LanguageIcon}
        infoContent={t('userProfileInfo.noteProcessingDesc')}
        onInfoClick={() => handleInfoClick('noteProcessing')}
      >
        <div className="pt-2.5 space-y-3">
          {/* Summarization Language */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('userProfile.summarizationLanguage')}</label>
            <select
              value={userLocale}
              onChange={(e) => handleLocaleSave(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
            >
              {SUPPORTED_LOCALES.map(locale => (
                <option key={locale.code} value={locale.code}>
                  {locale.name}
                </option>
              ))}
            </select>
          </div>

          {/* Summary Style */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">{t('userProfile.summaryStyle')}</label>
            <div className="space-y-1.5">
              {Object.values(SUMMARY_TYPES).map(type => (
                <label
                  key={type.value}
                  className={`flex items-start p-2 rounded-lg border cursor-pointer transition-colors ${
                    defaultSummaryType === type.value
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="summaryType"
                    value={type.value}
                    checked={defaultSummaryType === type.value}
                    onChange={() => handleSummaryTypeSave(type.value)}
                    className="mt-0.5 text-orange-500 focus:ring-orange-500"
                  />
                  <div className="ml-2">
                    <span className="block text-sm font-medium text-gray-900">{type.label}</span>
                    <span className="block text-xs text-gray-500">{type.description}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Note Length (Read-only) */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('userProfile.maximumNoteLength')}</label>
            <div className="px-2 py-1.5 bg-gray-100 rounded-lg text-sm text-gray-700 flex items-center gap-2">
              <ClockIcon className="h-4 w-4 text-gray-500" />
              {Math.floor((userData?.maxNoteLengthSec || 300) / 60)} {t('userProfile.minutes')}
            </div>
          </div>
        </div>
      </SettingsSection>

      {/* Email Preferences */}
      <SettingsSection 
        title={t('userProfile.emailPreferences')} 
        icon={BellIcon}
        infoContent={t('userProfileInfo.emailPreferencesDesc')}
        onInfoClick={() => handleInfoClick('emailPreferences')}
      >
        <div className="pt-2 divide-y divide-gray-100">
          <Toggle
            enabled={defaultSendEmail}
            onChange={handleDefaultSendEmailToggle}
            label={t('userProfile.sendEmailByDefault')}
          />
          <Toggle
            enabled={addTranscriptInEmail}
            onChange={handleTranscriptToggle}
            label={t('userProfile.includeTranscriptInEmail')}
          />
        </div>
      </SettingsSection>

      {/* Webhook Configuration */}
      <SettingsSection 
        title={t('userProfile.integrationsWebhooks')} 
        icon={LinkIcon} 
        defaultOpen={false}
        infoContent={t('userProfileInfo.webhooksDesc')}
        onInfoClick={() => handleInfoClick('webhooks')}
      >
        <div className="pt-2.5 space-y-3">
          
          {/* Enable/Disable */}
          <Toggle
            enabled={webhookEnabled}
            onChange={handleWebhookEnabledToggle}
            label={t('userProfile.enableWebhook')}
            description={t('userProfile.activateWebhookNotifications')}
          />

          {/* Webhook URL - Inline Edit */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-0.5">{t('userProfile.webhookURL')}</label>
            <InlineEdit
              value={webhookURL}
              onSave={handleWebhookURLSave}
              placeholder="https://your-webhook-url.com/endpoint"
              className="text-sm text-gray-700"
              textAlign="left"
              multiline={false}
              noPadding
            />
          </div>

          {/* Webhook Stats */}
          {(webhookStats.executions > 0 || webhookStats.errors > 0) && (
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 bg-green-50 rounded-lg">
                <p className="text-xs text-green-600 font-medium">{t('userProfile.totalExecutions')}</p>
                <p className="text-xl font-bold text-green-700">{webhookStats.executions}</p>
              </div>
              <div className="p-2 bg-red-50 rounded-lg">
                <p className="text-xs text-red-600 font-medium">{t('userProfile.totalErrors')}</p>
                <p className="text-xl font-bold text-red-700">{webhookStats.errors}</p>
              </div>
            </div>
          )}

          {/* Last Execution */}
          {webhookStats.lastExecutionDate && (
            <div className="p-2 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">{t('userProfile.lastExecution')}</p>
              <p className="text-sm text-gray-700">
                {new Date(webhookStats.lastExecutionDate?.toDate?.() || webhookStats.lastExecutionDate).toLocaleString()}
                {' - '}
                <span className=                  {webhookStats.lastExecutionResult === 'success' ? 'text-green-600' : 'text-red-600'}>
                  {webhookStats.lastExecutionResult === 'success' ? t('userProfile.success') : t('userProfile.error')}
                </span>
              </p>
              {webhookStats.lastError && (
                <p className="text-xs text-red-600 mt-1">{webhookStats.lastError}</p>
              )}
            </div>
          )}

          {/* View Logs Button */}
          <button
            onClick={handleFetchWebhookLogs}
            disabled={isLoadingLogs}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          >
            {isLoadingLogs ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
            ) : (
              <DocumentTextIcon className="h-4 w-4" />
            )}
            {showWebhookLogs ? t('userProfile.refreshLogs') : t('userProfile.viewExecutionLogs')}
          </button>

          {/* Webhook Logs */}
          {showWebhookLogs && (
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {webhookLogs.length > 0 ? (
                webhookLogs.map(log => (
                  <WebhookLogEntry key={log.id} log={log} />
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-3">{t('userProfile.noExecutionLogsFound')}</p>
              )}
            </div>
          )}
        </div>
      </SettingsSection>

      {/* Subscription Info (Read-Only) */}
      <SettingsSection title={t('userProfile.subscription')} icon={CreditCardIcon} defaultOpen={false}>
        <div className="pt-2.5 space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">{t('userProfile.currentPlan')}</span>
            <EditionBadge />
          </div>
          
          {/* Edition Benefits */}
          <div className="p-2.5 bg-gray-50 rounded-lg">
            <h4 className="text-xs font-medium text-gray-500 mb-1.5">{t('userProfile.yourPlanIncludes')}</h4>
            <ul className="space-y-1 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <CheckIcon className="h-4 w-4 text-green-500" />
                {t('userProfile.maxNoteLength')} {Math.floor((userData?.maxNoteLengthSec || 300) / 60)} {t('userProfile.minutes')}
              </li>
              {(userData?.edition === 'pro' || userData?.edition === 'founder') && (
                <li className="flex items-center gap-2">
                  <CheckIcon className="h-4 w-4 text-green-500" />
                  {t('userProfile.backgroundRecording')}
                </li>
              )}
            </ul>
          </div>

          {/* Subscription Details */}
          {userData?.subscriptionInfo && (
            <div className="space-y-1 text-sm">
              {userData.subscriptionInfo.expiresDate && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('userProfile.expires')}</span>
                  <span className="text-gray-700">
                    {new Date(userData.subscriptionInfo.expiresDate).toLocaleDateString()}
                  </span>
                </div>
              )}
              {userData.subscriptionInfo.isActive !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('userProfile.status')}</span>
                  <span className={userData.subscriptionInfo.isActive ? 'text-green-600' : 'text-red-600'}>
                    {userData.subscriptionInfo.isActive ? t('userProfile.active') : t('userProfile.inactive')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Stats */}
          {userData?.notesCreated > 0 && (
            <div className="pt-2 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                {t('userProfile.totalNotesCreated')} <span className="font-medium text-gray-700">{userData.notesCreated}</span>
              </p>
            </div>
          )}
        </div>
      </SettingsSection>

      {/* Help Section */}
      <SettingsSection title={t('userProfile.help')} icon={QuestionMarkCircleIcon} defaultOpen={false}>
        <div className="pt-2 divide-y divide-gray-100">
          <LinkRow
            icon={EnvelopeIcon}
            label={t('userProfile.contactSupport')}
            href={`mailto:${SUPPORT_EMAIL}?subject=NoteMail Support - User ID: ${auth.currentUser?.uid || 'unknown'}`}
          />
        </div>
      </SettingsSection>

      {/* Legal Section */}
      <SettingsSection title={t('userProfile.legal')} icon={ShieldCheckIcon} defaultOpen={false}>
        <div className="pt-2 divide-y divide-gray-100">
          <LinkRow
            icon={ShieldCheckIcon}
            label={t('userProfile.privacyPolicy')}
            href={PRIVACY_POLICY_URL}
          />
          <LinkRow
            icon={DocumentTextIcon}
            label={t('userProfile.termsOfService')}
            href={TERMS_OF_SERVICE_URL}
          />
          <LinkRow
            icon={UserMinusIcon}
            label={t('userProfile.deleteAccount')}
            href={DELETE_ACCOUNT_URL}
            danger
          />
        </div>
      </SettingsSection>

      {/* About Me Modal */}
      {showAboutMeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-900">{t('userProfile.editAboutMe')}</h3>
              <button
                onClick={() => setShowAboutMeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              {t('userProfile.describeYourself')}
            </p>
            <textarea
              value={aboutMe}
              onChange={(e) => setAboutMe(e.target.value)}
              className="w-full h-32 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
              placeholder={t('userProfile.exampleAboutMe')}
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={() => setShowAboutMeModal(false)}
                className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleAboutMeSave(aboutMe)}
                disabled={isSaving.aboutMe}
                className="px-3 py-1.5 text-sm text-white bg-[#F07807] rounded-lg hover:bg-[#d66a05] disabled:opacity-50 transition-colors"
              >
                {isSaving.aboutMe ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Keyword Modal */}
      {showKeywordModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-900">{t('userProfile.addKeyword')}</h3>
              <button
                onClick={() => setShowKeywordModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              {t('userProfile.addProperNouns')}
            </p>
            <input
              type="text"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddKeyword()}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder={t('userProfile.keywordExample')}
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={() => setShowKeywordModal(false)}
                className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleAddKeyword}
                disabled={!newKeyword.trim() || isSaving.hintKeywords}
                className="px-3 py-1.5 text-sm text-white bg-[#F07807] rounded-lg hover:bg-[#d66a05] disabled:opacity-50 transition-colors"
              >
                {isSaving.hintKeywords ? t('userProfile.adding') : t('common.add')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Modal */}
      <InfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        title={infoModalContent.title}
        content={infoModalContent.content}
      />
    </div>
  );
}

export default UserProfile;
